/**
 * De-dupes the realtime `FOLLOW_RECEIVED` echo against the actor's OWN
 * optimistic follow.
 *
 * When the viewer follows X, `applyFollowOptimistic` bumps X's `followers`
 * count immediately (instant feedback). X's notifications channel
 * (`morfi.user.${X}.notifications.v0`) — which the viewer's
 * `useGetFollowsCounts(X)` query subscribes to — then echoes a
 * `FOLLOW_RECEIVED` that `incrementFollowersCount` would apply a SECOND time,
 * double-counting on the actor's own screen.
 *
 * We can't de-dupe by comparing the event's `followerUserId` to the local
 * actor id: the actor id is not reliably populated (e.g. demo mode resolves it
 * to `null`). Instead we REMEMBER which users the local client just
 * optimistically followed and swallow the first echo for each.
 *
 * Correctness with concurrent follows: a mark is consumed by exactly one echo,
 * and the actor's own follow produces exactly one echo + one optimistic patch.
 * A different user's follow of X (a real new follower) produces its own echo;
 * since only one echo is swallowed per mark, the surplus echoes still apply.
 *
 * Platform-agnostic: pure JS state + `Date.now()`. No web/RN API. The state
 * hangs off `globalThis` (not a module-level `const`) so the optimistic path
 * (mutation chunk) and the realtime path (query chunk) share ONE map even if
 * the bundler duplicates this module across chunks.
 */

const ECHO_TTL_MS = 15_000;
const STORE_KEY = '__morfi_follow_echo_dedup__';

/** userId → expiry epoch ms, shared via `globalThis`. */
function store(): Map<string, number> {
  const g = globalThis as unknown as Record<string, unknown>;
  const existing = g[STORE_KEY];
  if (existing instanceof Map) return existing as Map<string, number>;
  const fresh = new Map<string, number>();
  g[STORE_KEY] = fresh;
  return fresh;
}

/** Record that the local client just optimistically followed `userId`. */
export function markOptimisticFollow(userId: string): void {
  store().set(userId, Date.now() + ECHO_TTL_MS);
}

/**
 * Returns true (and clears the mark) when `userId`'s realtime follow echo is
 * the local actor's own — i.e. it should be swallowed because the optimistic
 * patch already counted it. Expired marks are dropped and return false.
 */
export function consumeOptimisticFollowEcho(userId: string): boolean {
  const map = store();
  const expiry = map.get(userId);
  if (expiry === undefined) return false;
  map.delete(userId);
  return expiry > Date.now();
}
